import spartaEventsUtility from "utilities/global/sparta-events-utility/2.3.4/js/sparta-events-utility";
import spartaGlobalFunctions from "utilities/global/sparta-global-functions-utility/2.1.3/js/sparta-global-functions-utility";
import jQuery from "utilities/vendor/jquery/3.5.1/js/jquery";
import $ from "utilities/vendor/jquery/3.5.1/js/jquery";
import DataAssetLoader from "utilities/suits/data-asset-loader/2.1.1/js/data-asset-loader";
import spartaTransportUtility from "utilities/global/sparta-transport-utility/4.1.0/js/sparta-transport-utility";
/* global spartaGlobalFunctionsUtility */

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * @name        AML Validation Rules
 * @file        aml-validation-rules.js
 * @description Provides AML approved validation logic for inputs
 * @classdesc   Provides AML approved validation logic for inputs
 * @global      Creates a a global AMLValidationRules object
 * @author      James A. Black Jr.
 * @copyright   © 2017 Bank of America Corporation. All rights reserved.
 *
 * The AML Validation Rules is used to extend the Sparta Validation Framework, in order to support
 * a standard AML approved form validation experience across the Bank of America websites.  The
 * pre-defined validation rules each contain granular logic, intended to check for a multiple
 * validations.  By combining multiple rules together for an individual field, along with the
 * support for valid/invalid lists from external JSON files, each field will be validated against
 * a set of critera.  When a field fails a single validation rule, the error message displayed will
 * be the appropriate error message for that rule.
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/**
 * @const STRINGS
 * @description constant for storing common strings used in this utility
 */
const STRINGS = {
  UNITED_STATES: 'US',
  CANADA: 'CANADA',
  INTERNATIONAL: 'INTERNATIONAL',
  ENGLISH_LANGUAGE: 'en',
  SPANISH_LANGUAGE: 'es',
  GWIM: 'GWIM',
  US_TRUST: 'US TRUST',
  CED: 'CED',
  ISO2: 'ISO-2',
  ISO3: 'ISO-3',
  ISO3166: 'ISO-3166',
  CONTAINS: 'Contains',
  EXACT_MATCH: 'Exact Match',
  CITY_COUNTY_EN: 'Start with a letter and follow with only letters, spaces, periods (.) and hyphens (-).',
  CITY_COUNTY_ES: 'Comience con una letra y siga solo con letras, espacios, puntos (.) y guiones (-).',
};

class AMLValidationRules {
  init(validator, validationRulesImporter) {
    const self = this;

    this.language = spartaGlobalFunctions.getLanguage();
    this.importer = validationRulesImporter;
    this.validator = validator;

    /**
     * @const {Object} AML
     * @description a constant containing AML validation functions
     */
    const AML = {
      /* "validate.AML.firstName" method
       * "AML.firstName" validates that an input value contains only characters that are valid
       * for first name data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      maxLength (integer) [0]:  Optionally contain an integer, indicating
       *                          the maximum length. The integer must be greater than 0.
       *                          [Default: 40]
       *
       *                      allowSpecialChars (boolean) [1]:  A boolean value representing wether
       *                          or not to allow special characters. [Default: true]
       *
       * Returns:
       *    true  - if the input value matches the first name format
       *    false - if the input value does not match the first name format
       */
      firstName($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.firstName',
          keyName: 'name',
          // minLength: 2,
          maxLength: 40,
          maxRepeatedChar: ["a-zA-Z0-9 '.-", 2],
          maxRepeatExceptions: ['VIII', 'VIII.'],
          allowSpecialChars: true,
        };

        let options = {
          maxLength: defaults.maxLength,
          allowSpecialChars: defaults.allowSpecialChars,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        const validations = {
          // minLength: [options.minLength],
          nameCharacters: [options.allowSpecialChars],
          maxLength: [options.maxLength],
        };

        let validRepeatException = false;

        options.maxRepeatExceptions.every((value) => {
          const validException = $element.val().toUpperCase() === value;

          if (validException) {
            validRepeatException = true;

            return false;
          }

          return true;
        });

        if (!validRepeatException) {
          validations.maxRepeatedChar = options.maxRepeatedChar;
        }

        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults($element, options.ruleName,
          options.keyName);

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }

        if (validationsResult && !invalidListResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.middleName" method
       * "AML.middleName" validates that an input value contains only characters that are valid
       * for middle name data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      maxLength (integer) [0]:  Optionally contain an integer, indicating
       *                          the maximum length. The integer must be greater than 0.
       *                          [Default: 40]
       *
       *                      allowSpecialChars (boolean) [1]:  A boolean value representing wether
       *                          or not to allow special characters. [Default: true]
       *
       * Returns:
       *    true  - if the input value matches the middle name format
       *    false - if the input value does not match the middle name format
       */
      middleName($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.middleName',
          keyName: 'name',
          // minLength: 2,
          maxLength: 40,
          maxRepeatedChar: ["a-zA-Z0-9 '.-", 2],
          maxRepeatExceptions: ['VIII', 'VIII.'],
          allowSpecialChars: true,
        };

        let options = {
          maxLength: defaults.maxLength,
          allowSpecialChars: defaults.allowSpecialChars,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        const validations = {
          // minLength: [options.minLength],
          nameCharacters: [options.allowSpecialChars],
          maxLength: [options.maxLength],
        };

        let validRepeatException = false;

        options.maxRepeatExceptions.every((value) => {
          const validException = $element.val().toUpperCase() === value;

          if (validException) {
            validRepeatException = true;

            return false;
          }

          return true;
        });

        if (!validRepeatException) {
          validations.maxRepeatedChar = options.maxRepeatedChar;
        }

        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults($element, options.ruleName,
          options.keyName);

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }

        if (validationsResult && !invalidListResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.lastName" method
       * "AML.lastName" validates that an input value contains only characters that are valid
       * for last name data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      maxLength (integer) [0]:  Optionally contain an integer, indicating
       *                          the maximum length. The integer must be greater than 0.
       *                          [Default: 40]
       *
       *                      allowSpecialChars (boolean) [1]:  A boolean value representing wether
       *                          or not to allow special characters. [Default: true]
       *
       * Returns:
       *    true  - if the input value matches the last name format
       *    false - if the input value does not match the last name format
       */
      lastName($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.lastName',
          keyName: 'name',
          minLength: 2,
          maxLength: 40,
          maxRepeatedChar: ["a-zA-Z0-9 '.-", 2],
          allowSpecialChars: true,
        };

        let options = {
          maxLength: defaults.maxLength,
          allowSpecialChars: defaults.allowSpecialChars,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        const validations = {
          minLength: [options.minLength],
          nameCharacters: [options.allowSpecialChars],
          maxRepeatedChar: options.maxRepeatedChar,
          maxLength: [options.maxLength],
        };

        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults(
          $element,
          options.ruleName,
          options.keyName
        );

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }

        if (validationsResult && !invalidListResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.namePrefix" method
       * "AML.namePrefix" validates that an input value contains only characters that are valid
       * for first name data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters. (currently not used)
       *
       * Returns:
       *    true  - if the input value matches the first name format
       *    false - if the input value does not match the first name format
       */
      namePrefix($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.namePrefix',
          keyName: 'code',
        };

        const options = Object.assign({}, defaults);

        const validations = {
          namePrefix: [],
        };

        // Convert the input value to upper case.
        $element.val($element.val().toUpperCase());

        const validationsResult = self.getValidationResults($element, validations);
        const validListResult = self.getValidResults($element, options.ruleName, options.keyName);

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (validListResult instanceof Object) {
          return validListResult;
        }

        if (validationsResult && validListResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.nameSuffix" method
       * "AML.nameSuffix" validates that an input value contains only characters that are valid
       * for name suffix data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters. (currently not used)
       *
       * Returns:
       *    true  - if the input value matches the name suffix format
       *    false - if the input value does not match the name suffix format
       */
      nameSuffix($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.nameSuffix',
          keyName: 'code',
        };

        const options = Object.assign({}, defaults);

        const validations = {
          nameSuffix: [],
        };

        // Convert the input value to upper case.
        $element.val($element.val().toUpperCase());

        const validationsResult = self.getValidationResults($element, validations);
        const validListResult = self.getValidResults($element, options.ruleName, options.keyName);

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (validListResult instanceof Object) {
          return validListResult;
        }

        if (validationsResult && validListResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.nameGeneration" method
       * "AML.nameGeneration" validates that an input value contains only characters that are
       * valid for name generation data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters. (currently not used)
       *
       * Returns:
       *    true  - if the input value matches the name generation format
       *    false - if the input value does not match the name generation format
       */
      nameGeneration($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.nameGeneration',
          keyName: 'code',
        };

        const options = Object.assign({}, defaults);

        const validations = {
          nameGeneration: [],
        };

        // Convert the input value to upper case.
        $element.val($element.val().toUpperCase());

        const validationsResult = self.getValidationResults($element, validations);
        const validListResult = self.getValidResults($element, options.ruleName, options.keyName);

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (validListResult instanceof Object) {
          return validListResult;
        }

        if (validationsResult && validListResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.businessName" method
       * "AML.businessName" validates that an input value contains only characters that are
       * valid for business name data.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      maxLength (integer) [0]:  Optionally contain an integer, indicating
       *                          the maximum length. The integer must be greater than 0.
       *                          [Default: 60]
       *
       * Returns:
       *    true  - if the input value matches the business name format
       *    false - if the input value does not match the business name format
       */
      businessName($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.businessName',
          keyName: 'name',
          minLength: 3,
          maxLength: 60,
        };

        let options = {
          maxLength: defaults.maxLength,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        const validations = {
          minLength: [options.minLength],
          maxLength: [options.maxLength],
        };

        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults(
          $element,
          options.ruleName,
          options.keyName
        );

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }

        if (validationsResult && !invalidListResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.addressLine1" method
       * "AML.addressLine1" validates that an input value is formatted in the standard pattern
       * for street address values.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      maxLength (integer) [0]:  Optionally contain an integer, indicating
       *                          the maximum length. The integer must be greater than 0.
       *                          [Default: 30]
       *
       *                      validSpecialChars (string) [1]: The second parameter can optionally
       *                          contain a string including valid special characters.
       *                          [Default: "#/.,'-"]
       *
       *                      isMailingAddress (boolean) [2]: The third parameter is a boolean value
       *                          indicating if the address is a mailing address.  [Default: true]
       *
       * Returns:
       *    true  - if the input value matches the street address format
       *    false - if the input value does not match the street address format
       */
      addressLine1($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.addressLine1',
          keyName: 'address',
          validSpecialChars: "#/.,'-",
          isMailingAddress: true,
          minLength: 3,
          maxLength: 30,
        };

        let options = {
          maxLength: defaults.maxLength,
          validSpecialChars: defaults.validSpecialChars,
          isMailingAddress: defaults.isMailingAddress,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        const validations = {
          minLength: [options.minLength],
          intlStreetAddress: [options.validSpecialChars, options.isMailingAddress],
          maxLength: [options.maxLength],
        };

        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults($element, options.ruleName, options.keyName);

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }

        if (validationsResult && !invalidListResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.addressLine2" method
       * "AML.addressLine2" validates that an input value is formatted in the standard pattern
       * for street address values.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      maxLength (integer) [0]:  Optionally contain an integer, indicating
       *                          the maximum length. The integer must be greater than 0.
       *                          [Default: 30]
       *
       *                      validSpecialChars (string) [1]: The second parameter can optionally
       *                          contain a string including valid special characters.
       *                          [Default: "#/.,'-"]
       *
       *                      isMailingAddress (boolean) [2]: The third parameter is a boolean value
       *                          indicating if the address is a mailing address.  [Default: true]
       *
       * Returns:
       *    true  - if the input value matches the street address format
       *    false - if the input value does not match the street address format
       */
      addressLine2($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.addressLine2',
          keyName: 'address',
          validSpecialChars: "#/.,'-",
          isMailingAddress: true,
          minLength: 3,
          maxLength: 30,
        };

        let options = {
          maxLength: defaults.maxLength,
          validSpecialChars: defaults.validSpecialChars,
          isMailingAddress: defaults.isMailingAddress,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        const validations = {
          minLength: [options.minLength],
          intlStreetAddress: [options.validSpecialChars, options.isMailingAddress],
          maxLength: [options.maxLength],
        };

        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults($element, options.ruleName, options.keyName);

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }

        if (validationsResult && !invalidListResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.cityCounty" method
       * "AML.cityCounty" validates that an input value is formatted in the standard pattern for
       * a city or county name values.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      region (string) [0]: Optionally contain one of three string values for
       *                          the country/international ("US", "CANADA", "INTERNATIONAL").
       *                          [Default: "US"]
       *
       *                      minLength (integer) [1]: Optionally contain an integer, indicating the
       *                          maximum length. The integer must be greater than 0. [Default: 19]
       *
       * Returns:
       *    true  - if the input value matches the city/county format
       *    false - if the input value does not match the city/county format
       */
      cityCounty($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.cityCounty',
          keyName: 'city',
          minLength: 3,
          maxLength: 19,
          region: STRINGS.UNITED_STATES,
          validCharacters: null,
        };

        let options = {
          region: defaults.region,
          minLength: defaults.minLength,
          validCharacters: defaults.validCharacters,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        if (options.region === STRINGS.UNITED_STATES && !options.validCharacters) {
          options.validCharacters = '.-';
        }

        const validations = {
          minLength: [options.minLength],
          cityCounty: [options.region, options.validCharacters],
          maxLength: [options.maxLength],
        };

        if (options.region.toUpperCase() !== STRINGS.UNITED_STATES) {
          validations.minLength = [2];
        }

        const validationsResult = self.getValidationResults($element, validations);
        const invalidListResult = self.getInvalidResults($element, options.ruleName, options.keyName);

        if (validationsResult instanceof Object) {
          let result = {};

          if (options.region.toUpperCase() === STRINGS.UNITED_STATES && self.language === STRINGS.ENGLISH_LANGUAGE) {
            result.errorMessage = STRINGS.CITY_COUNTY_EN;
          } else if (options.region.toUpperCase() === STRINGS.UNITED_STATES && self.language === STRINGS.SPANISH_LANGUAGE) {
            result.errorMessage = STRINGS.CITY_COUNTY_ES;
          }

          result = Object.assign({}, validationsResult, result);

          return result;
        }

        if (invalidListResult instanceof Object) {
          return invalidListResult;
        }

        if (validationsResult && !invalidListResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.stateCode" method
       * "AML.stateCode" validates that an input value is formatted in the standard pattern for
       * state or province code values.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      region (string) [0]: Optionally contain one of three string values for
       *                          the country/international ("US", "CANADA", "INTERNATIONAL").
       *                          [Default: "US"]
       *
       * Returns:
       *    true  - if the input value matches the state code format
       *    false - if the input value does not match the state code format
       */
      stateCode($element, ruleParams) {
        let validListResult = false;

        const defaults = {
          ruleName: 'AML.stateCode',
          keyName: 'code',
          minLength: 2,
          maxLength: 2,
          region: STRINGS.UNITED_STATES,
        };

        let options = {
          region: defaults.region,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        const validations = {
          minLength: [options.minLength],
          stateCode: [options.region],
          maxLength: [options.maxLength],
        };

        // Convert the input value to upper case.
        $element.val($element.val().toUpperCase());

        if (options.region === STRINGS.UNITED_STATES) {
          validListResult = self.getValidResults($element, options.ruleName, options.keyName);
        }

        if (options.region === STRINGS.CANADA) {
          validations.stateCode = [STRINGS.CANADA];
          validListResult = true;
        }

        if (options.region === STRINGS.INTERNATIONAL) {
          validations.stateCode = [STRINGS.INTERNATIONAL];
          validations.maxLength = [5];
          validListResult = true;
        }

        const validationsResult = self.getValidationResults($element, validations);

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (validListResult instanceof Object) {
          return validListResult;
        }

        if (validationsResult && validListResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.zipCode" method
       * "AML.zipCode" validates that an input value is formatted in the standard pattern for a
       * US zip code (e.g., 12345 or 12345-6789).
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      region (string) [0]: Optionally contain one of three string values for
       *                          the country/international ("US", "CANADA", "INTERNATIONAL").
       *                          [Default: "US"]
       *
       *                      exceptionRule (string) [1]: A string value for LOB exceptions
       *                          (e.g. "GWIM", "US TRUST", "CED"). [Default: "GWIM"]
       *
       * Returns:
       *    true  - if the input value matches the zip code format
       *    false - if the input value does not match the zip code format
       */
      zipCode($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.zipCode',
          minLength: 5,
          maxLength: 5,
          region: STRINGS.UNITED_STATES,
          exceptionRule: STRINGS.GWIM,
          gwimExceptions: ['44444', '12345'],
          invalidZipCodes: ['00000', '11111', '22222', '33333', '55555', '66666',
            '77777', '88888', '99999', '54321'],
        };

        let options = {
          region: defaults.region,
          exceptionRule: defaults.exceptionRule,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        if (options.exceptionRule.toUpperCase() !== STRINGS.GWIM) {
          options.invalidZipCodes = options.invalidZipCodes.concat(options.gwimExceptions);
        }

        let validations = {
          minLength: [options.minLength],
          zip: [options.region],
          isNotValue: [options.invalidZipCodes],
          maxLength: [options.maxLength],
        };

        if (options.region.toUpperCase() !== STRINGS.UNITED_STATES) {
          validations = {
            minLength: [3],
            maxLength: [7],
          };

          // Convert the input value to upper case.
          $element.val($element.val().toUpperCase());

          if (options.region.toUpperCase() === STRINGS.INTERNATIONAL) {
            validations.maxLength = [10];
          }
        }

        const validationsResult = self.getValidationResults($element, validations);

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (validationsResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.countryCode" method
       * "AML.countryCode" validates that an input value is formatted in the standard pattern
       * for ISO-2 or ISO-3 country code values.
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      isoFormat (string) [0]: Optionally contain a string value representing
       *                          the format of the country code (e.g. "ISO-2", "ISO-3")
       *                          [Default: "ISO-2"]
       *
       * Returns:
       *    true  - if the input value matches the country code format
       *    false - if the input value does not match the country code format
       */
      countryCode($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.countryCode',
          minLength: 2,
          maxLength: 2,
          isoFormat: STRINGS.ISO2,
        };

        let options = {
          isoFormat: defaults.isoFormat,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        const validations = {
          minLength: [options.minLength],
          countryCode: [options.isoFormat],
          maxLength: [options.maxLength],
        };

        // Convert the input value to upper case.
        $element.val($element.val().toUpperCase());

        if (options.isoFormat.toUpperCase() !== STRINGS.ISO2) {
          validations.maxLength = [3];

          if (options.isoFormat.toUpperCase() === STRINGS.ISO3166) {
            validations.minLength = [1];
          }
        }

        const validListResult = self.getValidResults($element, options.ruleName, options.isoFormat);

        const validationsResult = self.getValidationResults($element, validations);

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (validListResult instanceof Object) {
          return validListResult;
        }

        if (validationsResult && validListResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.dateOfBirth" method
       * "AML.dateOfBirth" validates that an input value is formatted in the standard pattern
       * for a US date pattern (e.g., 01/01/1970) and the date is a valid date of birth.
       *
       * Parameters:
       *    $dateElement      - a jQuery reference to the date input element whose value is
       *                        being checked
       *    ruleParams        - an array holding any rule parameters.
       *
       *                        dateType (string) [0]: Optionally contains a date type string
       *                            (either "fullDate" ["mm/dd/yyyy"] or "monthYearDate"
       *                            ["mm/yyyy"]). [Default: "fullDate"]
       *
       *                        rangeException (boolean) [1]: A boolean value for date range
       *                            exception (e.g. true - if allow birth dates older than 125
       *                            years). [Default: false]
       *
       * Returns:
       *    true  - if the date value matches a valid date of birth format
       *    false - if the date value does not match a valid date of birth format
       */
      dateOfBirth($dateElement, ruleParams) {
        const defaults = {
          ruleName: 'AML.dateOfBirth',
          dateType: 'fullDate',
          rangeException: false,
          minDateRange: '-125y',
          maxDateRange: '0d',
        };

        let options = {
          dateType: defaults.dateType,
          rangeException: defaults.rangeException,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        let validations = {
          dateFormat: [options.dateType],
          dateRange: [options.minDateRange, options.maxDateRange, options.dateType],
        };

        // if the minimum date range exception is set to true, only validate the end date
        if (options.rangeException === true) {
          validations = {
            dateFormat: [options.dateType],
            endDate: [options.maxDateRange, options.dateType],
          };
        }

        const validationsResult = self.getValidationResults($dateElement, validations);

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (validationsResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.ssn" method
       * "AML.ssn" validates that an input value is formatted in the standard pattern for a
       * Social Security Number (e.g., 123-45-6789).
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      allowExceptions (boolean) [0]: A boolean value to allow invalid SSN
       *                          exceptions. [Default: false]
       *
       * Returns:
       *    true  - if the input value matches the SSN pattern
       *    false - if the input value does not match the SSN pattern
       */
      ssn($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.ssn',
          allowExceptions: false,
          invalidSSNExceptions: [/^000000000$/],
          invalidSSNPatterns: [
            /^300000000$/,
            /^666(\d{2})(\d{4})$/,
            /^000(\d{2})([1-9]\d{3})$/,
            /^000([1-9]\d{1})(\d{4})$/,
            /^(\d{3})00([1-9]\d{3})$/,
            /^([1-9]\d{2})00(\d{4})$/,
            /^(\d{3})([1-9]\d{1})0000$/,
            /^([1-9]\d{2})(\d{2})0000$/,
            /^9(\d{2})89(\d{4})$/,
            /^9(\d{2})[0-6](\d{1})(\d{4})$/,
          ],
        };

        let options = {
          allowExceptions: defaults.allowExceptions,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        const validations = {
          ssn: [],
        };

        const validationsResult = self.getValidationResults($element, validations);
        const elementValue = $element.val().replace(/\D/g, '');

        let invalidSSNResult = false;

        if (!options.allowExceptions) {
          options.invalidSSNPatterns =
            options.invalidSSNPatterns.concat(options.invalidSSNExceptions);
        }

        if (options.invalidSSNPatterns.length !== 0) {
          options.invalidSSNPatterns.every((invalidSSNPattern) => {
            invalidSSNResult = invalidSSNPattern.test(elementValue);

            if (invalidSSNResult) {
              return false;
            }

            return true;
          });
        }

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (validationsResult && !invalidSSNResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.tin" method
       * "AML.tin" validates that an input value is formatted in the standard pattern for a
       * Taxpayer Identification Number - Business (e.g., 123-45-6789).
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      allowExceptions (boolean) [0]: A boolean value to allow invalid TIN
       *                          exceptions. [Default: false]
       *
       * Returns:
       *    true  - if the input value matches the TIN pattern
       *    false - if the input value does not match the TIN pattern
       */
      tin($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.tin',
          allowExceptions: false,
          invalidTINExceptions: [/^000000000$/],
          invalidTINPatterns: [
            /^300000000$/,
            /^400000000$/,
            /^500000000$/,
            /^000(\d{2})([1-9]\d{3})$/,
            /^000([1-9]\d{1})(\d{4})$/,
            /^(\d{3})00([1-9]\d{3})$/,
            /^([1-9]\d{2})00(\d{4})$/,
            /^(\d{3})([1-9]\d{1})0000$/,
            /^([1-9]\d{2})(\d{2})0000$/,
            /^9(\d{2})93(\d{4})$/,
            /^9(\d{2})[7|8|9](\d{1})(\d{4})$/,
          ],
        };

        let options = {
          allowExceptions: defaults.allowExceptions,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        const validations = {
          ssn: [],
        };

        const validationsResult = self.getValidationResults($element, validations);
        const elementValue = $element.val().replace(/\D/g, '');

        let invalidTINResult = false;

        if (!options.allowExceptions) {
          options.invalidTINPatterns =
            options.invalidTINPatterns.concat(options.invalidTINExceptions);
        }

        if (options.invalidTINPatterns.length !== 0) {
          options.invalidTINPatterns.every((invalidTINPattern) => {
            invalidTINResult = invalidTINPattern.test(elementValue);

            if (invalidTINResult) {
              return false;
            }

            return true;
          });
        }

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (validationsResult && !invalidTINResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.itin" method
       * "AML.itin" validates that an input value is formatted in the standard pattern for a
       * Taxpayer Identification Number - Individual (e.g., 123-45-6789).
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array/object holding any rule parameters.
       *
       *                      allowExceptions (boolean) [0]: A boolean value to allow valid ITIN
       *                          exceptions. [Default: false]
       *
       * Returns:
       *    true  - if the input value matches the ITIN pattern
       *    false - if the input value does not match the ITIN pattern
       */
      itin($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.itin',
          allowExceptions: false,
          validITINExceptions: [/^000000000$/],
          validITINPatterns: [
            /^9(\d{2})([7][0-9]|[8][0-8]|[9][0-2,4-9])(\d{4})$/,
          ],
        };

        let options = {
          allowExceptions: defaults.allowExceptions,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        const validations = {
          ssn: [],
        };

        const validationsResult = self.getValidationResults($element, validations);

        const elementValue = $element.val().replace(/\D/g, '');

        let validITINResult = false;

        if (options.allowExceptions === true) {
          options.validITINExceptions.every((validITINException) => {
            validITINResult = validITINException.test(elementValue);

            if (validITINResult) {
              return false;
            }

            return true;
          });
        }

        if (!validITINResult && options.validITINPatterns.length !== 0) {
          options.validITINPatterns.every((validITINPattern) => {
            validITINResult = validITINPattern.test(elementValue);

            if (validITINResult) {
              return false;
            }

            return true;
          });
        }

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (validationsResult && validITINResult) {
          return true;
        }

        return false;
      },

      /* "validate.AML.ein" method
       * "AML.ein" validates that an input value is formatted in the standard pattern for a
       * Employer Identification Number (e.g., 12-3456789).
       *
       * Parameters:
       *    $element        - a jQuery reference to the input element whose value is being
       *                      checked
       *    ruleParams      - an array holding any rule parameters.
       *
       *                      allowExceptions (boolean) [0]: A boolean value to allow valid EIN
       *                          exceptions. [Default: false]
       *
       * Returns:
       *    true  - if the input value matches the EIN pattern
       *    false - if the input value does not match the EIN pattern
       */
      ein($element, ruleParams) {
        const defaults = {
          ruleName: 'AML.ein',
          allowExceptions: false,
          invalidEINExceptions: [/^000000000$/],
          invalidEINPatterns: [],
        };

        let options = {
          allowExceptions: defaults.allowExceptions,
        };

        options = validator.fn.helper.getDefinedParams(ruleParams, options);
        options = Object.assign({}, defaults, options);

        const validations = {
          ein: [],
        };

        const validationsResult = self.getValidationResults($element, validations);

        const elementValue = $element.val().replace(/\D/g, '');

        let invalidEINResult = false;

        if (!options.allowExceptions) {
          options.invalidEINPatterns =
            options.invalidEINPatterns.concat(options.invalidEINExceptions);
        }

        if (options.invalidEINPatterns.length !== 0) {
          options.invalidEINPatterns.every((invalidEINPattern) => {
            invalidEINResult = invalidEINPattern.test(elementValue);

            if (invalidEINResult) {
              return false;
            }

            return true;
          });
        }

        if (validationsResult instanceof Object) {
          return validationsResult;
        }

        if (validationsResult && !invalidEINResult) {
          return true;
        }

        return false;
      },
    };

    return AML;
  }

  /**
   * @method getValidationResults
   * @description Executes the validation with given parameters for each rule within the validations
   * object.
   * @param {Object} $element - the jQuery input element used to run each validation against
   * @param {Object} validations - an object containing each rule and given parameters
   * @returns {Object|Boolean} validationResult
   */
  getValidationResults($element, validations) {
    let validationResult = true;

    Object.keys(validations).every((ruleName) => {
      const ruleParams = validations[ruleName];

      validationResult = this.validator.fn.validate[ruleName]($element, ruleParams);

      if (!validationResult) {
        validationResult = { ruleName, ruleParams };

        return false;
      }

      return true;
    });

    return validationResult;
  }

  /**
   * @method getInvalidResults
   * @description Checks to see if the $element value matches a list of invalid values.
   * @param {Object} $element - the jQuery input element used to run each validation against
   * @param {String} ruleName - name of the rule to retrieve the list of invalid values
   * @param {String} keyName - a string representing the key name for a invalid value
   * @returns {Boolean} invalidResult
   */
  getInvalidResults($element, ruleName, keyName, matchType, caseSensitive = false) {
    let invalidResult = false;

    const elementValue = $element.val();
    const invalidValues = this.importer.RULES[ruleName].list;

    if (invalidValues && invalidValues.length !== 0) {
      invalidValues.every((invalidValue) => {
        let exactMatchType = invalidValue.type === STRINGS.EXACT_MATCH;
        let containsType = invalidValue.type === STRINGS.CONTAINS;

        if (matchType) {
          exactMatchType = matchType === STRINGS.EXACT_MATCH;
          containsType = matchType === STRINGS.CONTAINS;
        }

        if (exactMatchType) {
          invalidResult = (caseSensitive) ? elementValue === invalidValue[keyName] :
            elementValue.toUpperCase() === invalidValue[keyName].toUpperCase();
        } else if (containsType) {
          invalidResult = (caseSensitive) ? elementValue.indexOf(invalidValue[keyName]) >= 0 :
            elementValue.toUpperCase().indexOf(invalidValue[keyName].toUpperCase()) >= 0;
        }

        if (invalidResult) {
          invalidResult = { ruleName, ruleParams: [elementValue] };

          return false;
        }

        return true;
      });
    } else {
      if (window.console && console.warn) {
        console.warn(`Sparta Validation: ${ruleName} list empty.`);
      }

      return { ruleName, ruleParams: [elementValue] };
    }

    return invalidResult;
  }

  /**
   * @method getValidResults
   * @description Checks to see if the $element value matches a list of valid values.
   * @param {Object} $element - the jQuery input element used to run each validation against
   * @param {String} ruleName - name of the rule to retrieve the list of valid values
   * @param {String} keyName - a string representing the key name for a valid value
   * @returns {Boolean} validResult
   */
  getValidResults($element, ruleName, keyName, caseSensitive = false) {
    let validResult = false;

    const elementValue = $element.val();
    const validValues = this.importer.RULES[ruleName].list;

    if (validValues && validValues.length !== 0) {
      validValues.every((validValue) => {
        if (caseSensitive) {
          validResult = elementValue === validValue[keyName];
        } else {
          validResult = elementValue.toUpperCase() === validValue[keyName].toUpperCase();
        }

        if (validResult) {
          return false;
        }

        return true;
      });
    } else if (window.console && console.warn) {
        console.warn(`Sparta Validation: ${ruleName} list empty.`);
    }

    if (validResult) {
      return true;
    }

    return { ruleName, ruleParams: [elementValue] };
  }
}

const AMLValidations = new AMLValidationRules();

export default AMLValidations;
